import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { MapPin, Calendar, Clock } from "lucide-react";
import { useInfiniteArticles } from "../api/hooks/useArticles";
import { useTags } from "../api/hooks/useTags";
import { assetUrl } from "../api/client";
import useScrollToTop from "../hooks/useScrollToTop";
import {
  getTranslatedTitle,
  getFirstCountryName,
} from "../api/utils/translations";

const PAGE_SIZE = 9;

const Blog = () => {
  const { t, i18n } = useTranslation();
  useScrollToTop();
  const [selectedTags, setSelectedTags] = useState([]);
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteArticles({
    limit: PAGE_SIZE,
  });

  const posts = useMemo(
    () => data?.pages?.flatMap((page) => page?.data || []) || [],
    [data]
  );

  const uiLang = (i18n.language || "en").split("-")[0];

  // Color map for tags
  const tagColors = [
    "#3B82F6", // Blue
    "#10B981", // Green
    "#F59E0B", // Yellow
    "#EF4444", // Red
    "#8B5CF6", // Purple
    "#F97316", // Orange
    "#06B6D4", // Cyan
    "#84CC16", // Lime
    "#EC4899", // Pink
    "#6366F1", // Indigo
  ];

  // Fetch tags from API
  const { data: tagsData, isLoading: tagsLoading } = useTags();

  // Process tags with colors and translations
  const allTags = useMemo(() => {
    if (!tagsData || !Array.isArray(tagsData)) return [];

    return tagsData.map((tag, index) => {
      const lang = (i18n.language || "en").split("-")[0];
      let translatedName = tag.name;

      if (tag.translations && Array.isArray(tag.translations)) {
        const translation = tag.translations.find((tr) => {
          const langCode = tr.languages_code;
          if (lang === "en" && langCode === "en-US") return true;
          if (lang === "sk" && langCode === "sk-SK") return true;
          return false;
        });

        if (translation) {
          translatedName = translation.text;
        }
      }

      return {
        id: tag.id,
        name: translatedName,
        color: tagColors[index % tagColors.length],
      };
    });
  }, [tagsData, i18n.language]);

  // Filter posts by selected tags
  const filteredPosts = useMemo(() => {
    if (selectedTags.length === 0) return posts;

    return posts.filter((post) => {
      if (!post.tags || !Array.isArray(post.tags)) return false;

      return selectedTags.some((selectedTagId) =>
        post.tags.some((tag) => tag.Tags_id?.id === selectedTagId)
      );
    });
  }, [posts, selectedTags]);

  // Load more when the bottom spinner enters the viewport
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleTagClick = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const showLoadMoreSpinner = hasNextPage;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t("blog.title")}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("blog.subtitle")}
            </p>
          </div>
        </div>
      </div>

      {/* Tags Filter */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            {tagsLoading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : allTags.length > 0 ? (
              <>
                <div className="flex flex-wrap justify-center gap-2">
                  {allTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => handleTagClick(tag.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                          isSelected
                            ? "text-white shadow-md scale-105"
                            : "text-gray-600 hover:scale-105 hover:shadow-sm"
                        }`}
                        style={{
                          backgroundColor: isSelected ? tag.color : "#F3F4F6",
                          border: isSelected ? "none" : "1px solid #D1D5DB",
                        }}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="mt-3 text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    {t("blog.clearTags", { defaultValue: "Clear all tags" })}
                  </button>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-sm">
                {t("blog.noTags", { defaultValue: "No tags available" })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t("blog.noStories")}
            </h3>
            <p className="text-gray-600">{t("blog.noStoriesSubtitle")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => {
              const imgId = post.cover_image?.id || post.cover_image;
              const imgSrc = imgId
                ? assetUrl(imgId, { width: 800, height: 450, fit: "cover" })
                : undefined;
              const country = getFirstCountryName(post.countries, "—", uiLang);
              const date = post.published_at
                ? new Date(post.published_at).toLocaleDateString()
                : "";
              const title = getTranslatedTitle(
                post.translations,
                uiLang,
                t("common.untitled", { defaultValue: "Untitled" })
              );

              return (
                <Card
                  key={post.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video overflow-hidden bg-muted">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        {t("common.noImage", { defaultValue: "No image" })}
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <span className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {country}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {date}
                      </span>
                    </div>
                    <CardTitle className="text-xl">
                      <Link
                        to={`/blog/${post.id}`}
                        className="hover:text-primary transition-colors"
                      >
                        {title}
                      </Link>
                    </CardTitle>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {post.tags.map((tag, index) => {
                          const tagColor = tagColors[index % tagColors.length];

                          let tagName = tag.Tags_id?.name || "Unknown";
                          if (
                            tag.Tags_id?.translations &&
                            Array.isArray(tag.Tags_id.translations)
                          ) {
                            const lang = (i18n.language || "en").split("-")[0];
                            const translation = tag.Tags_id.translations.find(
                              (tr) => {
                                const langCode = tr.languages_code;
                                if (lang === "en" && langCode === "en-US")
                                  return true;
                                if (lang === "sk" && langCode === "sk-SK")
                                  return true;
                                return false;
                              }
                            );

                            if (translation) {
                              tagName = translation.text;
                            }
                          }

                          return (
                            <span
                              key={tag.id}
                              className="px-2 py-1 text-xs rounded-full font-medium"
                              style={{
                                backgroundColor: `${tagColor}20`,
                                color: tagColor,
                                border: `1px solid ${tagColor}40`,
                              }}
                            >
                              {tagName}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.read_time || "—"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/90"
                      >
                        <Link to={`/blog/${post.id}`}>
                          {t("blog.readMore")}
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Infinite scroll sentinel — spinner only while more pages exist */}
        {showLoadMoreSpinner && (
          <div
            ref={loadMoreRef}
            className="flex justify-center py-10"
            aria-hidden={!isFetchingNextPage}
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
